import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type {
  ApplicationRemarkType,
  ApplicantInfoFormValues,
} from '../createNomination/types';
import { type FundingSourceConfiguration } from '../../../components';

const GET_APPLICANT_INFORMATION = gql`
  query ($id: ID!, $applicationId: String!) {
    getFundingSourceConfiguration(applicationId: $applicationId) {
      mapping
      validTags
    }

    researchAssessment(applicationId: $applicationId) {
      type
      _id
    }

    application(id: $id) {
      _id
      applicationNo
      applicant {
        _id
        name {
          givenName
          surname
        }
      }
      programmeChoice {
        entryYear
        programmeType
        programme

        department
        partner {
          university
          proposedChiefSupervisor
        }
        rejectedByFirstChoice
        researchArea
        proposedChiefSupervisor
        researchAreaBackup
        proposedChiefSupervisorBackup
      }
      status
      remarks {
        fromRole
        toRole
        remark
        createdAt
      }
    }
  }
`;

type GetApplicantInfo = {
  applicationInfoData: ApplicantInfoFormValues;
  fundingSourceConfiguration: FundingSourceConfiguration;
  isFinishedResearchAssessment: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export const getApplicantInformation = (id: string): GetApplicantInfo => {
  const {
    data: applicantInfo,
    loading,
    error,
    refetch,
  } = useQuery(GET_APPLICANT_INFORMATION, {
    variables: { id, applicationId: id },
    skip: !id,
  });

  const {
    _id: applicationId,
    applicationNo: applicationNumber,
    applicant,
    programmeChoice,
    status,
    remarks: returnRemarks,
  } = applicantInfo?.application ?? {};
  const applicantName = `${applicant?.name?.surname?.toUpperCase()} ${applicant?.name.givenName}`;
  const applicantId = applicant?._id;
  const {
    entryYear,
    programmeType,
    programme,
    proposedChiefSupervisor: proposedSupervisor,
    researchArea,
    partner,
    department,
    rejectedByFirstChoice = false,
    researchAreaBackup,
    proposedChiefSupervisorBackup,
  } = programmeChoice ?? {};
  const {
    university: partnerInstitution,
    proposedChiefSupervisor: chiefSupervisorOfPartnerInstitution,
  } = partner ?? {};

  const remarks: ApplicationRemarkType[] = returnRemarks ?? [];
  const applicationInfoData: ApplicantInfoFormValues = {
    applicationId,
    applicantId,
    applicationNumber,
    applicantName,
    entryYear,
    programmeType,
    programme,
    department,
    researchArea,
    partnerInstitution,
    chiefSupervisorOfPartnerInstitution,
    proposedSupervisor,
    rejectedByFirstChoice,
    researchAreaBackup,
    proposedChiefSupervisorBackup,
    status,
    remarks,
  };

  // fundingSourceConfiguration
  const fundingSourceConfiguration: FundingSourceConfiguration = {
    mapping: {
      COMPETITIVE:
        applicantInfo?.getFundingSourceConfiguration?.mapping?.COMPETITIVE ??
        [],
      NON_COMPETITIVE:
        applicantInfo?.getFundingSourceConfiguration?.mapping
          ?.NON_COMPETITIVE ?? [],
      OTHER: applicantInfo?.getFundingSourceConfiguration?.mapping?.OTHER ?? [],
    },
    validTags: applicantInfo?.getFundingSourceConfiguration?.validTags ?? [],
  };

  const publicationResearchAssessment: Array<{
    type: string;
    publicationId: string;
  }> = (applicantInfo?.researchAssessment ?? []).map((assessment: any) => ({
    type: assessment?.type ?? '',
    publicationId: assessment?._id ?? '',
  }));

  const isFinishedResearchAssessment =
    !publicationResearchAssessment.length ||
    publicationResearchAssessment.every(assess => assess.type);

  return {
    applicationInfoData,
    fundingSourceConfiguration,
    isFinishedResearchAssessment,
    loading,
    error,
    refetch,
  };
};
